import { getCacheBuster, poll } from 'api/shared';
import { exportTypes } from 'constants/values';
import networkAdapter from '../adapters/network';
import { kebabCaseKeys } from 'util/mapKeys';
import { orchestrationLayerFetcher } from 'util/OL';
import { gql } from 'graphql-request';
import { trackError } from 'util/error';

export const fetchScriptByIdTankMix = async ({ fieldId, scriptId }) => {
  const query = gql`
    query prescription($input:PrescriptionInput!) {
      prescription(input:$input) {
        activityType
        activityUuid
        croppingYear
        createTime
        fieldId
        fieldUuid
        fmzId
        fmzName
        fmzVersion
        id
        name
        primary
        season
        summary {
          zones {
            id
            area {
              q
              u
            }
            description
          }
          application {
            product {
              id
              name
              brand
            }
            area {
              q
              u
            }
            rate {
              q
              u
            }
            incorporationMethod
          }
          planting {
            product {
              uuid
              name
              brand
            }
          }
          fertility {
            product {
              id
              name
              brand
            }
          }
        }
        updateTime
        attributes {
          crop
        }
        bases {
          name
        }
        geometry
        warnings {
          code
          message
        }
        tag
        programInstanceId
      }
    }
  `;
  const variables = {
    input: {
      prescriptionId: scriptId,
      fieldId: fieldId
    }
  };

  try {
    const { prescription } = await orchestrationLayerFetcher(query, variables);
    const response = kebabCaseKeys(prescription);
    response.geometry = kebabCaseKeys(JSON.parse(prescription.geometry));
    return response;
  } catch (error) {
    trackError(new TypeError('Could not fetch "prescription" query from OL'), error);
  }
};

export const fetchManyScripts = ( {userId, fieldId, scriptIds} ) => {
  if( !fieldId ) {
    // this in particular triggers a misleading 401, so avoid that
    return Promise.reject('No fieldId sent to groot for script list');
  }
  const legacyOptions = {
    url: `/api/groot/v2/bulk/prescriptions/${fieldId}`,
    userId,
    method: 'POST',
    redirectsToLoginOnAuthFail: false,
    body: JSON.stringify({'prescription-uuids': scriptIds })
  };

  return networkAdapter({
    legacyOptions
  });
};

// migration to OL for feature to support tank mix
export const fetchManyScriptsTankMix = async ({ fieldId, scriptIds }) => {
  if (!fieldId) {
    // this in particular triggers a misleading 401, so avoid that
    return Promise.reject('No fieldId sent to groot for script list');
  }
  const query = gql`
    query bulkPrescriptions($input: BulkPrescriptionInput!) {
      bulkPrescriptions(input: $input) {
        fieldUuid
        primary
        fmzId
        season
        fmzName
        id
        createTime
        tag
        fieldId
        activityUuid
        croppingYear
        fmzVersion
        activityType
        name
        updateTime
        warnings {
          code
          message
        }
        attributes {
          crop
        }
        programInstanceId
        bases {
          name
        }
        geometry
        summary {
          zones {
            id
            area {
              q
              u
            }
            description
          }
          application {
            incorporationMethod
            product {
              id
              name
              brand
            }
          }
          planting {
            product {
              uuid
              name
              brand
            }
          }
          fertility {
            product {
              id
              name
              brand
            }
          }
        }
      }
    }
  `;
  const variables = {
    input: {
      prescriptionUuids: scriptIds,
      fieldId
    }
  };

  try {
    const result = await orchestrationLayerFetcher(query, variables);
    const { bulkPrescriptions: scripts } = result;
    const processed = scripts.reduce((result, script) =>
      [...result, { ...script, geometry: JSON.parse(script.geometry) }], []);
    return { result: kebabCaseKeys(processed) };
  } catch (error) {
    trackError(new TypeError('Could not fetch "bulkPrescriptions" query from OL'), error);
  }
};

export const fetchDuplicateManualScriptTemplate = ( {activityUuid, fieldId, scriptId, userId } ) => {
  const queryString = activityUuid ? `?activity-uuid=${activityUuid}` : '';

  return poll(() => {
    const legacyOptions = {
      url: `/api/groot/v2/prescriptions/template/${fieldId}/${scriptId}${queryString}`,
      userId,
      redirectsToLoginOnAuthFail: false
    };

    return networkAdapter({
      legacyOptions
    });
  });
};

export const fetchStatusDownloadScript = ({ jobUuid, userId, downloadType=exportTypes.SINGLE }) => {
  const statusPath = downloadType === exportTypes.BULK ? 'push' : 'download/status';
  return poll(() => {
    const legacyOptions = {
      url: `/api/mycroft/v2/prescriptions/${statusPath}/${jobUuid}?bustCache=${getCacheBuster()}`,
      userId
    };

    return networkAdapter({
      legacyOptions
    });
  });
};
